import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../services/settings.service';
import { QueryKeys } from '../config/query-client';

const SETTINGS_CACHE_KEY = 'gym-settings';
const SETTINGS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days — matches offline grace period

/** Check whether a value looks like real settings data (not a queued-mutation stub). */
const isValidSettings = (v) =>
  v && typeof v === 'object' && !v.queued && !v.data?.queued;

/** Read + validate settings from localStorage, returning null if missing or corrupt. */
const readLocalSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (!isValidSettings(parsed)) {
      localStorage.removeItem(SETTINGS_CACHE_KEY);
      return null;
    }
    // Enforce TTL — discard if expired
    if (parsed._cachedAt && Date.now() - parsed._cachedAt > SETTINGS_TTL) {
      localStorage.removeItem(SETTINGS_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(SETTINGS_CACHE_KEY);
    return null;
  }
};

/** Write settings to localStorage with a timestamp so the TTL can be enforced. */
const writeLocalSettings = (settings) => {
  try {
    localStorage.setItem(
      SETTINGS_CACHE_KEY,
      JSON.stringify({ ...settings, _cachedAt: Date.now() })
    );
  } catch {
    // storage full or unavailable — non-critical
  }
};

const SettingsContext = createContext(null);

export const useSettingsContext = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettingsContext must be used within SettingsProvider');
  return ctx;
};

export const SettingsProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [localSettings, setLocalSettings] = useState(readLocalSettings);
  const [hasLoadedFromApi, setHasLoadedFromApi] = useState(false);

  // Fetch settings from API (only if not in localStorage or forced)
  const { data: apiSettings, isLoading } = useQuery({
    queryKey: QueryKeys.SETTINGS,
    queryFn: () => settingsService.getSettings(),
    enabled: !localSettings || !hasLoadedFromApi,
    staleTime: 5 * 60 * 1000,
  });

  // Update local state when API data arrives
  useEffect(() => {
    if (apiSettings && !hasLoadedFromApi) {
      setLocalSettings(apiSettings);
      writeLocalSettings(apiSettings);
      setHasLoadedFromApi(true);
    }
  }, [apiSettings, hasLoadedFromApi]);

  // Mutation to update settings
  const updateSettingsMutation = useMutation({
    mutationFn: (data) => settingsService.updateSettings(data),
    onMutate: async (newSettings) => {
      // Optimistically update local state
      const updatedSettings = { ...localSettings, ...newSettings };
      setLocalSettings(updatedSettings);
      writeLocalSettings(updatedSettings);
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QueryKeys.SETTINGS });
      
      const previousSettings = queryClient.getQueryData(QueryKeys.SETTINGS);
      queryClient.setQueryData(QueryKeys.SETTINGS, updatedSettings);
      
      return { previousSettings };
    },
    onError: (err, newSettings, context) => {
      // Revert on error
      if (context?.previousSettings) {
        setLocalSettings(context.previousSettings);
        writeLocalSettings(context.previousSettings);
        queryClient.setQueryData(QueryKeys.SETTINGS, context.previousSettings);
      }
    },
    onSuccess: (data) => {
      // When the mutation was queued (offline), the queued-response metadata
      // must NOT overwrite the optimistically-updated local/RQ caches.
      if (data?.queued) return;
      // Update with server response
      setLocalSettings(data);
      writeLocalSettings(data);
      queryClient.setQueryData(QueryKeys.SETTINGS, data);
    },
  });

  const updateSettings = (data) => {
    updateSettingsMutation.mutate(data);
  };

  const value = {
    settings: localSettings || { disableLockers: true, disableTrainers: true, disableTelegram: true },
    isLoading,
    updateSettings,
    isUpdating: updateSettingsMutation.isPending,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
